import React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { BarChart, BarChartHorizontal, PieChart, MapPin, SquareStack, Filter, ChevronRight, TrendingUp, ChevronDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import PropertyTable from './PropertyTable';
import PropertyMap from './PropertyMap';
import PriceDistributionChart from './PriceDistributionChart';
import PricePerSqMeterChart from './PricePerSqMeterChart';
import PropertyTypeChart from './PropertyTypeChart';
import NeighborhoodChart from './NeighborhoodChart';
import PriceVariationChart from './PriceVariationChart';
import { Property } from '@/types/property';

interface DashboardTabsProps {
  loading: boolean;
  dataLoaded: boolean;
  databaseError: string | null;
  filteredProperties: Property[];
  isFilterExpanded: boolean;
  onToggleFilterExpand: () => void;
  isMobile?: boolean;
  dashboardType?: 'venda' | 'aluguel' | 'sairam-ultima-semana' | 'entraram-ultima-semana';
}

const DashboardTabs: React.FC<DashboardTabsProps> = ({ 
  loading,
  dataLoaded,
  databaseError,
  filteredProperties,
  isFilterExpanded,
  onToggleFilterExpand,
  isMobile = false,
  dashboardType = 'venda'
}) => {
  const priceLabel = (dashboardType === 'venda' || dashboardType === 'sairam-ultima-semana' || dashboardType === 'entraram-ultima-semana') ? 'Preços' : 'Aluguéis';

  return (
    <div className="space-y-3 md:space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 md:h-9"
            onClick={onToggleFilterExpand}
          >
            <Filter className="h-4 w-4" />
            <span className="ml-2 hidden md:inline">Filtros</span>
            {isFilterExpanded ? (
              <ChevronDown className="ml-1 h-4 w-4 md:ml-2" />
            ) : (
              <ChevronRight className="ml-1 h-4 w-4 md:ml-2" />
            )}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="lista" className="space-y-3 md:space-y-4">
        <TabsList>
          <TabsTrigger value="lista">Lista</TabsTrigger>
          <TabsTrigger value="mapa">Mapa</TabsTrigger>
          <TabsTrigger value="graficos">Gráficos</TabsTrigger>
        </TabsList>

        <TabsContent value="lista">
          <PropertyTable 
            loading={loading} 
            dataLoaded={dataLoaded} 
            databaseError={databaseError} 
            properties={filteredProperties} 
            dashboardType={dashboardType}
          />
        </TabsContent>

        <TabsContent value="mapa">
          <PropertyMap 
            loading={loading} 
            dataLoaded={dataLoaded} 
            databaseError={databaseError} 
            properties={filteredProperties} 
            dashboardType={dashboardType}
          />
        </TabsContent>

        <TabsContent value="graficos" className="space-y-3 md:space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <Card>
              <CardHeader className="pb-2 pt-3 px-3 md:pb-3 md:pt-4 md:px-4">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <BarChart className="h-4 w-4 md:h-5 md:w-5" />
                  Distribuição de Preços
                </CardTitle>
              </CardHeader>
              <CardContent className="h-60 md:h-80 p-2 md:p-4">
                {loading && !dataLoaded ? (
                  <div className="h-full flex items-center justify-center">
                    <Skeleton className="h-full w-full" />
                  </div>
                ) : databaseError ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p className="text-sm">Não foi possível carregar os dados para o gráfico.</p>
                  </div>
                ) : filteredProperties.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p className="text-sm">Dados insuficientes para exibir o gráfico</p>
                  </div>
                ) : (
                  <PriceDistributionChart properties={filteredProperties} isMobile={isMobile} dashboardType={dashboardType} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 pt-3 px-3 md:pb-3 md:pt-4 md:px-4">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <SquareStack className="h-4 w-4 md:h-5 md:w-5" />
                  Distribuição de Preços por m²
                </CardTitle>
              </CardHeader>
              <CardContent className="h-60 md:h-80 p-2 md:p-4">
                {loading && !dataLoaded ? (
                  <div className="h-full flex items-center justify-center">
                    <Skeleton className="h-full w-full" />
                  </div>
                ) : databaseError ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p className="text-sm">Não foi possível carregar os dados para o gráfico.</p>
                  </div>
                ) : filteredProperties.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p className="text-sm">Dados insuficientes para exibir o gráfico</p>
                  </div>
                ) : (
                  <PricePerSqMeterChart properties={filteredProperties} isMobile={isMobile} dashboardType={dashboardType} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 pt-3 px-3 md:pb-3 md:pt-4 md:px-4">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <PieChart className="h-4 w-4 md:h-5 md:w-5" />
                  Distribuição por Tipo
                </CardTitle>
              </CardHeader>
              <CardContent className="h-60 md:h-80 p-2 md:p-4">
                {loading && !dataLoaded ? (
                  <div className="h-full flex items-center justify-center">
                    <Skeleton className="h-full w-full" />
                  </div>
                ) : databaseError ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p className="text-sm">Não foi possível carregar os dados para o gráfico.</p>
                  </div>
                ) : filteredProperties.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p className="text-sm">Dados insuficientes para exibir o gráfico</p>
                  </div>
                ) : (
                  <PropertyTypeChart properties={filteredProperties} isMobile={isMobile} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 pt-3 px-3 md:pb-3 md:pt-4 md:px-4">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <TrendingUp className="h-4 w-4 md:h-5 md:w-5" />
                  Variação de Preços
                </CardTitle>
              </CardHeader>
              <CardContent className="h-60 md:h-80 p-2 md:p-4">
                {loading && !dataLoaded ? (
                  <div className="h-full flex items-center justify-center">
                    <Skeleton className="h-full w-full" />
                  </div>
                ) : databaseError ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p className="text-sm">Não foi possível carregar os dados para o gráfico.</p>
                  </div>
                ) : filteredProperties.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p className="text-sm">Dados insuficientes para exibir o gráfico</p>
                  </div>
                ) : (
                  <PriceVariationChart properties={filteredProperties} isMobile={isMobile} dashboardType={dashboardType} />
                )}
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader className="pb-2 pt-3 px-3 md:pb-3 md:pt-4 md:px-4">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <BarChartHorizontal className="h-4 w-4 md:h-5 md:w-5" />
                  Top Bairros com Preço por /m²
                </CardTitle>
              </CardHeader>
              <CardContent className="h-80 md:h-96 p-4">
                {loading && !dataLoaded ? (
                  <div className="h-full flex items-center justify-center">
                    <Skeleton className="h-full w-full" />
                  </div>
                ) : databaseError ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p>Não foi possível carregar os dados para o gráfico.</p>
                  </div>
                ) : filteredProperties.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    <p>Dados insuficientes para exibir o gráfico</p>
                  </div>
                ) : (
                  <NeighborhoodChart properties={filteredProperties} dashboardType={dashboardType} />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DashboardTabs;
